/**
 * Вибір варіації на сторінці товару: фінальна ціна, залишок і доступність опцій.
 *
 * Модуль нічого не рахує сам — він перемикає готові рядки. Формат валюти
 * (символ, роздільники, дописаний код) живе в налаштуваннях магазину й у
 * фільтрі 'woocommerce_price_format'; збирати його ще й тут означало б мати два
 * джерела правди, які розійдуться при першій же зміні налаштувань. Тому PHP
 * друкує в сторінку весь реєстр варіацій (nmhg_product_variations()), а JS
 * вибирає з нього потрібне.
 *
 * Початковий стан — теж від PHP: обрані опції й вимкнені кнопки вже стоять у
 * розмітці (nmhg_product_attribute_groups()), тож сторінка не блимає доступними
 * опціями до виконання скрипта. Тут повторюється рівно те саме правило, але
 * для наступних перемикань.
 *
 * ── Зіставлення ──────────────────────────────────────────────────────────
 *
 * Варіація описана набором `attribute_{слаг} => значення`. Порожнє значення —
 * це «будь-яке» (опція «Any …» у WooCommerce): така варіація підходить під усі
 * значення цього атрибута. Атрибут, якого ще не обрали, теж не суперечить —
 * тому перевірка йде по атрибутах ВАРІАЦІЇ, а не по вибору.
 *
 * ── Доступність опцій ────────────────────────────────────────────────────
 *
 * Опція доступна, коли існує варіація в наявності, яка має це значення й не
 * суперечить решті вибору. Через «решту вибору» правило й працює на кількох
 * групах: зміна одного атрибута перераховує доступність усіх інших.
 *
 * Якщо після перерахунку обрана опція виявилась вимкненою, вибір переїжджає на
 * першу доступну в тій же групі — і перерахунок повторюється, бо це знову
 * змінює контекст для сусідніх груп. Кількість проходів обмежена: у здорових
 * даних вистачає одного-двох, а межа страхує від зациклення на суперечливому
 * наборі варіацій.
 */

const MAX_PASSES = 5;

const readJson = (form, selector, fallback) => {
  const holder = form.querySelector(selector);

  if (!holder) return fallback;

  try {
    return JSON.parse(holder.textContent) ?? fallback;
  } catch {
    // Зіпсований JSON — помилка рендера, а не стан сторінки. Тихо лишаємо
    // фолбек: ціна вгорі на місці, сторінка робоча.
    return fallback;
  }
};

/** Усі радіо-групи форми: `[{ name, inputs: [...] }]`, у порядку розмітки. */
const readGroups = (form) => {
  const groups = new Map();

  form.querySelectorAll('input[type="radio"][name^="attribute_"]').forEach((input) => {
    if (!groups.has(input.name)) groups.set(input.name, []);
    groups.get(input.name).push(input);
  });

  return [...groups].map(([name, inputs]) => ({ name, inputs }));
};

const selectionOf = (groups) => {
  const chosen = {};

  groups.forEach(({ name, inputs }) => {
    const checked = inputs.find((input) => input.checked);
    if (checked) chosen[name] = checked.value;
  });

  return chosen;
};

const matches = (variation, selection) =>
  Object.entries(variation.attrs).every(([name, value]) => {
    if (value === '' || !(name in selection)) return true;

    return selection[name] === value;
  });

const optionAvailable = (variations, selection, name, value) => {
  const probe = { ...selection, [name]: value };

  return variations.some((variation) => variation.in_stock && matches(variation, probe));
};

const setOptionState = (input, disabled) => {
  input.disabled = disabled;

  const label = input.closest('.order-commodity__option');

  if (!label) return;

  label.classList.toggle('is-disabled', disabled);
  label.setAttribute('tabindex', disabled ? '-1' : '0');
};

const initForm = (form) => {
  const variations = readJson(form, '[data-variations]', null);

  if (!Array.isArray(variations) || !variations.length) return;

  const i18n = readJson(form, '[data-variations-i18n]', {});
  const groups = readGroups(form);

  if (!groups.length) return;

  const block = form.querySelector('[data-final-price]');
  const priceOut = form.querySelector('[data-final-price-value]');

  // Залишок живе не в блоці ціни, а в блоці кількості — там для нього вже є
  // місце у верстці ('.order-commodity__availability' праворуч від панелі).
  const stockOut = form.querySelector('[data-variation-stock]');
  const panel = form.querySelector('[data-quantity]');

  /**
   * Розставляє disabled по всіх опціях і, за потреби, пересуває вибір на
   * доступну. Повертає true, якщо вибір довелося змінити — тоді потрібен ще
   * один прохід.
   */
  const refreshOptions = () => {
    const selection = selectionOf(groups);
    let moved = false;

    groups.forEach(({ name, inputs }) => {
      inputs.forEach((input) => {
        setOptionState(input, !optionAvailable(variations, selection, name, input.value));
      });

      const checked = inputs.find((input) => input.checked);

      if (checked && checked.disabled) {
        const next = inputs.find((input) => !input.disabled);

        if (next) {
          next.checked = true;
          moved = true;
        }
      }
    });

    return moved;
  };

  const showPrice = () => {
    if (!block || !priceOut) return;

    const selection = selectionOf(groups);
    const complete = groups.every(({ name }) => name in selection);
    const found = complete ? variations.find((variation) => matches(variation, selection)) : null;

    if (!found || !found.now) {
      block.hidden = true;
      priceOut.textContent = '';
      if (stockOut) stockOut.textContent = '';
      return;
    }

    // Класи ті самі, що в ціни вгорі: розводить їх custom.scss, бо
    // '.commodity-price span' у main.scss закреслює будь-який <span>, а
    // wc_price() загортає число саме в нього.
    priceOut.innerHTML =
      `<span class="price-now">${found.now}</span>` +
      (found.was ? `<span class="price-was">${found.was}</span>` : '');

    if (stockOut) {
      stockOut.textContent = found.stock === null ? '' : `${found.stock} ${i18n.inStock ?? ''}`.trim();
    }

    // Стеля лічильника кількості — залишок саме цієї варіації. Без обліку
    // (stock === null) межі немає, і атрибут знімається зовсім.
    if (panel) {
      if (found.stock === null) {
        delete panel.dataset.max;
      } else {
        panel.dataset.max = String(found.stock);
      }

      panel.dispatchEvent(new CustomEvent('nmhg:max-changed'));
    }

    block.hidden = false;
  };

  const render = () => {
    for (let pass = 0; pass < MAX_PASSES && refreshOptions(); pass += 1);

    showPrice();
  };

  form.addEventListener('change', (event) => {
    if (event.target.matches('input[type="radio"][name^="attribute_"]')) render();
  });

  render();
};

const init = () => document.querySelectorAll('form.order-commodity').forEach(initForm);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
